import { defineComponent as _defineComponent } from 'vue'

import { Select, Option } from 'element-ui'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import ChecklistStepPanel from './ChecklistStepPanel.vue'

const __default__ = {
  name: 'checklist-instance',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    Breadcrumb,
    BreadcrumbItem,
    ChecklistStepPanel
  }
}

import { ref, reactive, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { formattedDate, time } from '@/plugins/dateFormatter'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const loading = ref(true)
const run = ref<any>({})
const steps = ref<any[]>([])
const selectedId = ref<number | null>(null)
const drawerOpen = ref(false)
const isWide = ref(true)
const review = reactive({ show: false, decision: 'approve', note: '', saving: false })
const alert = reactive({ show: false, title: '', message: '', variant: 'warning', redirect: '', remove: false })

const completedCount = computed(() => steps.value.filter(s => s.is_completed === 1).length)
const issueCount = computed(() => steps.value.filter(s => s.issue_flagged === 1).length)
const progressPercent = computed(() => steps.value.length ? Math.round((completedCount.value / steps.value.length) * 100) : 0)
const selectedIndex = computed(() => steps.value.findIndex(s => s.id === selectedId.value))
const selectedStep = computed(() => selectedIndex.value >= 0 ? steps.value[selectedIndex.value] : null)
// Sign-off needs its own permission. A runner never reviews their own run unless they are an admin or manager (mirrors the lambda rule).
const profile = JSON.parse(localStorage.getItem('profile') || '{}')
const isAdminOrManager = Number(profile.default_role_id) === 1 || Number(profile.default_role_id) === 2
const canReview = computed(() => {
  if (!hasPermissions('checklist_history_approve')) return false
  const isOwner = Number(run.value?.r_id) === Number(profile.id)
  return !isOwner || isAdminOrManager
})
// Same gate the run page has: anyone who can run checklists can tag. The server enforces it.
const canTag = computed(() => hasPermissions('checklists_submit') || hasPermissions('checklists_view'))
const canCreateTicket = computed(() => hasPermissions('project_ticket_update'))
const catalog = ref<any[]>([])
const tagsSaving = reactive<Record<string, boolean>>({ run: false })
const projects = ref<any[]>([])
const ticketOptions = reactive<{ statuses: any[]; ticket_types: any[]; users: any[] }>({ statuses: [], ticket_types: [], users: [] })
const priorities = [
  { id: 1, name: 'Highest' }, { id: 2, name: 'High' }, { id: 3, name: 'Medium' }, { id: 4, name: 'Low' }, { id: 5, name: 'Lowest' }
]
const ticket = reactive<any>({ show: false, step: null, projectId: null, title: '', description: '', status: null, priority: 3, type: null, assignee: null, titleError: '', saving: false, loadingProjects: false })
const totalTime = computed(() => minutesBetween(run.value.start_time, run.value.timestamp))

const mql = window.matchMedia('(min-width: 992px)')
function onMedia() { isWide.value = mql.matches }

function hasPermissions(scope: string) {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === scope)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function parseError(err: any): string {
  const data = err?.response?.data
  if (!data) return 'Something went wrong'
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || data } catch (e) { return data }
  }
  return data.error || 'Something went wrong'
}

function showAlert(title: string, message: string, variant = 'warning', redirect = '') {
  alert.title = title
  alert.message = message
  alert.variant = variant
  alert.redirect = redirect
  alert.show = true
}
function onAlertConfirm() {
  alert.show = false
  if (alert.redirect) proxy.$router.push(alert.redirect)
}
function handleError(err: any) {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    proxy.$router.push('/forbidden')
    return
  }
  if (status === 404) {
    showAlert('Run not found', 'This run no longer exists.', 'warning', '/checklists/history?count=10&page=1')
    return
  }
  showAlert('Error', parseError(err), 'danger')
}

function statusLabel(status: string) {
  const labels: Record<string, string> = { in_progress: 'In progress', completed: 'Completed', awaiting_approval: 'Awaiting approval', approved: 'Approved', rejected: 'Rejected' }
  return labels[status] || 'Completed'
}
function statusPillClass(status: string) {
  const classes: Record<string, string> = { in_progress: 'cr-pill--info', completed: 'cr-pill--success', awaiting_approval: 'cr-pill--warning', approved: '', rejected: 'cr-pill--danger' }
  return classes[status] ?? 'cr-pill--success'
}
function sessionDate(d: string) { return formattedDate(group.date_format, d) }
function sessionTime(t: string) { return time(group.time_format, t) }

function minutesBetween(start: string, end: string) {
  if (!start || !end) return '-'
  const mins = Math.ceil(Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 60000)
  return mins === 1 ? '1 minute' : mins + ' minutes'
}
function minutesSinceStart(at: string) {
  if (!at || !run.value.start_time) return ''
  const mins = Math.max(0, Math.round((new Date(at).getTime() - new Date(run.value.start_time).getTime()) / 60000))
  return '+' + mins + ' min'
}

function selectStep(step: any) {
  selectedId.value = step.id
  if (!isWide.value) drawerOpen.value = true
}

async function loadRun() {
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/checklists/instances/${proxy.$route.query.id}`, config)
    run.value = response.data.run
    steps.value = response.data.steps
    if (selectedId.value === null && steps.value.length) selectedId.value = steps.value[0].id
  } catch (err) {
    handleError(err)
  } finally {
    loading.value = false
  }
}

async function loadCatalog() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/tags`, config)
    catalog.value = Array.isArray(response.data) ? response.data : []
  } catch (e) {
    catalog.value = []
  }
}

async function setTags(target: 'run' | 'step', step: any, ids: number[]) {
  const key = target === 'run' ? 'run' : 's' + step.id
  if (tagsSaving[key]) return
  tagsSaving[key] = true
  const holder = target === 'run' ? run.value : step
  const previous = holder.tags || []
  try {
    const config = await authConfig()
    const response = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'set_tags', target, step_id: target === 'step' ? step.id : undefined, tag_ids: ids }, config)
    holder.tags = response.data.tags || []
  } catch (err: any) {
    holder.tags = previous
    proxy.$notify({ type: 'danger', message: 'Failed to update tags', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    tagsSaving[key] = false
  }
}
function addTag(target: 'run' | 'step', step: any, tagId: number) {
  const holder = target === 'run' ? run.value : step
  const current = (holder.tags || []).map((t: any) => t.id)
  if (current.includes(tagId)) return
  setTags(target, step, [...current, tagId])
}
function removeTag(target: 'run' | 'step', step: any, tag: any) {
  const holder = target === 'run' ? run.value : step
  setTags(target, step, (holder.tags || []).map((t: any) => t.id).filter((id: number) => id !== tag.id))
}

// ── Create ticket from a flagged step ──────────────────────────────────────
function escapeHtml(text: string) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
async function openTicketDrawer(step: any) {
  ticket.step = step
  ticket.projectId = null
  ticket.status = null
  ticket.type = null
  ticket.assignee = null
  ticket.priority = 3
  ticket.titleError = ''
  ticket.title = `${run.value.checklist_name}: ${step.name}`
  const lines = [
    `Checklist: ${run.value.checklist_name}`,
    `Step: ${step.name}`
  ]
  if (step.issue_flagged === 1) lines.push(`Issue: ${step.issue_description || 'Flagged without a description'}`)
  if (step.note) lines.push(`Note: ${step.note}`)
  if (step.photo_urls && step.photo_urls.length) lines.push('Photos:', ...step.photo_urls)
  lines.push(`Run: ${window.location.origin}/checklists/history/instance?id=${run.value.id}`)
  ticket.description = lines.join('\n')
  ticket.show = true
  if (projects.value.length === 0) {
    ticket.loadingProjects = true
    try {
      const config = await authConfig()
      const response = await axios.get(`${baseUrl}/projects?count=200&offset=0`, config)
      projects.value = response.data.projects || []
    } catch (e) {
      projects.value = []
    } finally {
      ticket.loadingProjects = false
    }
  }
}
async function onProjectChange(projectId: number) {
  ticket.projectId = projectId
  ticket.status = null
  ticket.type = null
  ticket.assignee = null
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/projects/${projectId}?tab=0&count=1&offset=0`, config)
    ticketOptions.statuses = response.data.statuses || []
    ticketOptions.ticket_types = response.data.ticket_types || []
    ticketOptions.users = response.data.users || []
    ticket.status = ticketOptions.statuses.length ? ticketOptions.statuses[0].id : null
    ticket.type = ticketOptions.ticket_types.length ? ticketOptions.ticket_types[0].id : null
  } catch (err) {
    handleError(err)
  }
}
async function submitTicket() {
  ticket.titleError = ''
  if (!ticket.title.trim()) {
    ticket.titleError = 'Give the ticket a title'
    return
  }
  ticket.saving = true
  try {
    const config = await authConfig()
    const linkify = (line: string) => line.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
    const description = escapeHtml(ticket.description).split('\n').map((l: string) => `<p>${linkify(l) || '&nbsp;'}</p>`).join('')
    const created = await axios.post(`${baseUrl}/projects/${ticket.projectId}/tickets`, {
      title: ticket.title.trim(),
      description,
      assignee: ticket.assignee || null,
      status: ticket.status,
      priority: ticket.priority,
      ticket_type: ticket.type,
      time_estimate_minutes: null,
      due_date: null
    }, config)
    const ticketId = created.data && created.data.id
    const project = projects.value.find((pj: any) => pj.id === ticket.projectId)
    const identifier = (project && project.identifier ? project.identifier + '-' : '') + (created.data && created.data.identifier)
    let linked = false
    for (let attempt = 0; attempt < 2 && !linked; attempt += 1) {
      try {
        await axios.put(`${baseUrl}/checklists/instances`, { id: run.value.id, action: 'link_ticket', step_id: ticket.step.id, work_ticket_id: ticketId }, config)
        linked = true
      } catch (e) { /* retry once */ }
    }
    ticket.show = false
    if (linked) {
      proxy.$notify({ type: 'success', message: `Ticket ${identifier} created`, icon: 'tim-icons icon-check-2' })
      await loadRun()
    } else {
      showAlert('Ticket created but not linked', `Ticket ${identifier} was created but could not be linked to the step. Open it from the project board.`, 'warning')
    }
  } catch (err) {
    handleError(err)
  } finally {
    ticket.saving = false
  }
}

const reviewAction = computed(() => {
  if (review.decision === 'approve') return 'Approve'
  return run.value && run.value.status === 'awaiting_approval' ? 'Send back' : 'Reject'
})
const reviewTitle = computed(() => (review.decision === 'approve' ? 'Approve run' : (reviewAction.value === 'Send back' ? 'Send back' : 'Reject run')))

function openReview(decision: string) {
  review.decision = decision
  review.note = ''
  review.show = true
}

async function submitReview() {
  review.saving = true
  try {
    const config = await authConfig()
    const res = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'review', decision: review.decision, review_note: review.note }, config)
    review.show = false
    const newStatus = res.data && res.data.status
    const message = newStatus === 'in_progress' ? 'Run sent back' : (review.decision === 'approve' ? 'Run approved' : 'Run rejected')
    proxy.$notify({ type: 'success', message, icon: 'tim-icons icon-check-2' })
    await loadRun()
  } catch (err) {
    handleError(err)
  } finally {
    review.saving = false
  }
}

async function deleteRun() {
  alert.remove = false
  loading.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/checklists/instances/${run.value.id}`, config)
    proxy.$notify({ type: 'success', message: 'Run deleted', icon: 'tim-icons icon-check-2' })
    proxy.$router.push('/checklists/history?count=10&page=1')
  } catch (err) {
    loading.value = false
    handleError(err)
  }
}

onMounted(() => {
  onMedia()
  mql.addEventListener('change', onMedia)
  loadCatalog()
  loadRun()
})
onBeforeUnmount(() => { mql.removeEventListener('change', onMedia) })

return { __sfc: true,proxy, companyId, group, baseUrl, loading, run, steps, selectedId, drawerOpen, isWide, review, alert, completedCount, issueCount, progressPercent, selectedIndex, selectedStep, profile, isAdminOrManager, canReview, canTag, canCreateTicket, catalog, tagsSaving, projects, ticketOptions, priorities, ticket, totalTime, mql, onMedia, hasPermissions, authConfig, parseError, showAlert, onAlertConfirm, handleError, statusLabel, statusPillClass, sessionDate, sessionTime, minutesBetween, minutesSinceStart, selectStep, loadRun, loadCatalog, setTags, addTag, removeTag, escapeHtml, openTicketDrawer, onProjectChange, submitTicket, reviewAction, reviewTitle, openReview, submitReview, deleteRun, Breadcrumb, BreadcrumbItem, ChecklistStepPanel, formatGroupDate, formatGroupTime }
}

})